import { Mediator, payload } from "modular-openscriptjs";
import { gc, dc } from "../ojs.contexts";
import { api, STRINGS, uc } from "../declarations";
import {
    deleteFromStorage,
    empty,
    getStore,
    inform,
    refreshOtpVerifiedAt,
    saveUser,
    store,
} from "../helpers";
import { loadGoogleMaps } from "../google-maps";
import { $e } from "../ojs.events";

export default class System extends Mediator {
    $$product = {
        stored: async () => {
            await this.fetchNotificationCount();
            setTimeout(() => this.fetchNotificationCount(), 60000);
        },
    };

    $$system = {
        booted: async () => {
            await this.loadAppConfigs();

            loadGoogleMaps(import.meta.env.VITE_GOOGLE_MAPS_KEY);

            gc.appLoaded.value = true;
            gc.appLoadedAt.value = (new Date()).getTime();

            this.send($e.app.loaded);

            this.fetchNotificationCount();

            if (gc.auth.value && empty(window.companies)) {
                app("router").to("business.register");
                return;
            }
        },

        needs: {
            configReload: async () => {
                await this.loadAppConfigs();
                gc.appConfigReloaded.value = true;
                gc.appLoadedAt.value = (new Date).getTime();

                this.send($e.app.reloaded);

                this.fetchNotificationCount();

                if (gc.auth.value && empty(window.companies)) {
                    app("router").to("business.register");
                    return;
                }
            },

            notificationCount: () => this.fetchNotificationCount(),
        },

        has: {
            notification: (ed, event) => {
                this.fetchNotificationCount();
            },
        },
    };

    $$branch = {
        created_deleted: () => this.send($e.system.needs.configReload),
    };

    async loadAppConfigs() {
        let res = await api.get("business/configs");

        if (!res) {
            inform(
                "Oops! We could not open the app. Please try again.",
                STRINGS.WARNING,
                5000,
                false,
            );

            return;
        }

        let configs = res.data;

        for (let key in configs) {
            window[key] = configs[key];
        }

        gc.user.value = window.currentUser;
        gc.auth.value = window.isLoggedIn;

        // Re-scope the OTP grace period to whichever user this config
        // load resolved to — covers boot, config reloads after
        // login/logout, and any other account switch.
        await refreshOtpVerifiedAt(window.currentUser?.id);

        await saveUser(window.currentUser);
        uc.settings.value = window.userSettings;

        await this.refreshLocalStorage(configs);
    }

    async fetchNotificationCount() {
        if (!gc.auth.value || !window.currentCompany?.id) return;

        try {
            const res = await api.get(
                `companies/${window.currentCompany.id}/notifications/count`,
            );
            if (res && res.data) {
                dc.notificationsCount.value = res.data;
            }
        } catch (e) {
            console.error("Failed to load notification counts", e);
        }
    }

    async refreshLocalStorage(configs) {
        await saveUser(configs.user);
        await store("user_companies", configs.companies);
        await store("user_settings", configs.userSettings);
        let currentCompany = await getStore("current_company");
        let previousCompany = null;

        if (empty(configs.companies)) {
            await deleteFromStorage("current_company");
            await deleteFromStorage("user_companies");
            delete window.currentCompany;
            delete window.companies;
            dc.companies.value = [];
        } else {
            dc.companies.value = configs.companies;

            if (currentCompany) {
                // check if this company is among the user companies
                let company = configs.companies.find(
                    (c) => c.id == currentCompany.id,
                );

                if (empty(company)) {
                    previousCompany = currentCompany;
                    currentCompany = configs.companies[0] ?? null;

                    await store("current_company", currentCompany);

                    window.currentCompany = currentCompany;
                } else {
                    window.currentCompany = company;
                    await store("current_company", company);
                }
            }
        }

        this.send(
            $e.company.has.changed,
            payload({ previous: previousCompany, current: currentCompany }),
        );
    }
}
